import { InfoTip } from "@/components/InfoTip";
import { useTaxonomy } from "@/lib/useTaxonomy";
import { Label } from "@/components/ui/label";

const SELECT_CLASS = "mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm";

/** Profession → specialty picker with consumer-friendly tooltips and optional extra specialties. */
export const ProfessionPicker = ({ profession, primarySpecialty, additional, onChange }) => {
  const professions = useTaxonomy();
  const current = professions.find((p) => p.key === profession);
  const specialtyMeta = current?.specialties.find((s) => s.name === primarySpecialty);
  const specialtyHasOwnTip = specialtyMeta && specialtyMeta.headline !== current?.headline;
  const extraOptions = professions.flatMap((p) =>
    p.specialties.filter((s) => s.name !== primarySpecialty).map((s) => ({ ...s, profession: p.label }))
  );

  const toggleExtra = (name) =>
    onChange({
      additional_specialties: additional.includes(name)
        ? additional.filter((s) => s !== name)
        : [...additional, name],
    });

  return (
    <>
      <div>
        <div className="flex items-center gap-1.5">
          <Label htmlFor="sp-profession">Profession</Label>
          {current && <InfoTip headline={current.headline} summary={current.summary} testId="sp-profession-info" />}
        </div>
        <select
          id="sp-profession"
          className={SELECT_CLASS}
          value={profession}
          onChange={(e) => onChange({ profession: e.target.value, primary_specialty: "", additional_specialties: [] })}
          data-testid="sp-profession-select"
        >
          <option value="">Select your profession</option>
          {professions.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
        {current && <p className="mt-1.5 text-xs text-slate-500">{current.headline} Regulated by {current.board}.</p>}
      </div>

      <div>
        <div className="flex items-center gap-1.5">
          <Label htmlFor="sp-specialty">Specialty</Label>
          {specialtyHasOwnTip && (
            <InfoTip headline={specialtyMeta.headline} summary={specialtyMeta.summary} testId="sp-specialty-info" />
          )}
        </div>
        <select
          id="sp-specialty"
          className={SELECT_CLASS}
          value={primarySpecialty}
          disabled={!current}
          onChange={(e) => onChange({ primary_specialty: e.target.value })}
          data-testid="sp-specialty-select"
        >
          <option value="">{current ? "Select your specialty" : "Choose a profession first"}</option>
          {(current?.specialties || []).map((s) => (
            <option key={s.name} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
        {specialtyHasOwnTip && <p className="mt-1.5 text-xs text-slate-500">{specialtyMeta.summary}</p>}
      </div>

      {current && (
        <div>
          <p className="text-sm font-medium text-slate-700">
            Additional specialties <span className="text-xs font-normal text-slate-500">(optional)</span>
          </p>
          <p className="mt-1 text-xs text-slate-500">Tick anything else you are qualified and licensed to deliver.</p>
          <div className="mt-2 flex max-h-56 flex-wrap gap-2 overflow-y-auto" data-testid="sp-additional-specialties">
            {extraOptions.map((s) => (
              <button
                key={s.name}
                type="button"
                onClick={() => toggleExtra(s.name)}
                title={`${s.profession} — ${s.headline}`}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  additional.includes(s.name)
                    ? "border-emerald-600 bg-emerald-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-emerald-300"
                }`}
                data-testid={`sp-extra-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
